import { cn } from '@/lib/cn';

/**
 * LoadingGrid — four pulsing book-card skeletons laid out in a horizontal
 * row that mirrors a sync-mode `BookCarousel`. Used by the Library page
 * while a semantic search request is in flight so the layout doesn't
 * collapse between "hero" and "results" states.
 *
 * Sized to match `BookCard` (240×264) so swapping the skeletons for real
 * results causes zero layout shift.
 */
export interface LoadingGridProps {
  /** Defaults to 4 per docs/LIBRARY.md. */
  count?: number;
  className?: string;
}

export function LoadingGrid({ count = 4, className }: LoadingGridProps) {
  return (
    <div
      className={cn('flex gap-4 pb-3', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading search results"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex w-[140px] flex-shrink-0 flex-col gap-2 sm:w-[240px]" aria-hidden>
          <div className="aspect-[240/264] w-full animate-pulse rounded-xl bg-or-slate-200" />
          <div className="h-3 w-4/5 animate-pulse rounded bg-or-slate-200" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-or-slate-100" />
        </div>
      ))}
    </div>
  );
}
